// Chinese Zodiac data and logic (delegates to unified easternCompatibility module)
import {
  CHINESE_ANIMALS as EC_ANIMALS,
  CHINESE_ELEMENTS as EC_ELEMENTS,
  getChineseAnimal as ecGetChineseAnimal,
  getChineseElement as ecGetChineseElement,
  calculateChineseCompatibilityProfiles as ecCalculateChineseCompatibilityProfiles,
  generateChineseMatchesBase as ecGenerateChineseMatchesBase,
  normalizeAnimalName as ecNormalizeAnimalName,
} from './easternCompatibility';

// Import shared numerology module
import {
  getLifePathNumber as sharedGetLifePathNumber,
  calculateLifePathCompatibility,
  generateNumerologyMatches as sharedGenerateNumerologyMatches,
  LIFE_PATH_MEANINGS,
  COMPATIBILITY_MATRIX
} from './numerology';

export const chineseZodiac = {
    // Canonical lists
    animals: EC_ANIMALS,
    elements: EC_ELEMENTS,

    // Wrapper preserving legacy 1-arg support
    getAnimal: function(year, month, day) {
        if (arguments.length === 1) {
            return this.animals[(year - 1900) % 12];
        }
        return ecGetChineseAnimal(year, month, day);
    },
    
    getElement: function(year) {
        return ecGetChineseElement(year);
    },
};

// Unified Chinese compatibility API (profiles-based)
export function calculateChineseCompatibility(profile1, profile2) {
    const result = ecCalculateChineseCompatibilityProfiles(profile1, profile2);
    // Keep return shape with description for UI
    return { score: result.score, description: result.description, breakdown: result.breakdown };
}

// Western Zodiac data and logic
export const westernZodiac = {
    signs: [
        { name: 'Capricorn', start: [12, 22], end: [1, 19], element: 'Earth' },
        { name: 'Aquarius', start: [1, 20], end: [2, 18], element: 'Air' },
        { name: 'Pisces', start: [2, 19], end: [3, 20], element: 'Water' },
        { name: 'Aries', start: [3, 21], end: [4, 19], element: 'Fire' },
        { name: 'Taurus', start: [4, 20], end: [5, 20], element: 'Earth' },
        { name: 'Gemini', start: [5, 21], end: [6, 20], element: 'Air' },
        { name: 'Cancer', start: [6, 21], end: [7, 22], element: 'Water' },
        { name: 'Leo', start: [7, 23], end: [8, 22], element: 'Fire' },
        { name: 'Virgo', start: [8, 23], end: [9, 22], element: 'Earth' },
        { name: 'Libra', start: [9, 23], end: [10, 22], element: 'Air' },
        { name: 'Scorpio', start: [10, 23], end: [11, 21], element: 'Water' },
        { name: 'Sagittarius', start: [11, 22], end: [12, 21], element: 'Fire' }
    ],
    
    getSign: function(month, day) {
        for (let sign of this.signs) {
            if (this.isDateInRange(month, day, sign.start, sign.end)) {
                return sign;
            }
        }
        return this.signs[0];
    },
    
    isDateInRange: function(month, day, start, end) {
        if (start[0] <= end[0]) {
            return (month > start[0] || (month === start[0] && day >= start[1])) &&
                   (month < end[0] || (month === end[0] && day <= end[1]));
        } else {
            return (month > start[0] || (month === start[0] && day >= start[1])) ||
                   (month < end[0] || (month === end[0] && day <= end[1]));
        }
    },
    
    compatibility: {
        'Fire': { compatible: ['Fire', 'Air'], neutral: ['Earth'], challenging: [], enemy: ['Water'] },
        'Earth': { compatible: ['Earth', 'Water'], neutral: ['Fire'], challenging: [], enemy: ['Air'] },
        'Air': { compatible: ['Air', 'Fire'], neutral: ['Water'], challenging: [], enemy: ['Earth'] },
        'Water': { compatible: ['Water', 'Earth'], neutral: ['Air'], challenging: [], enemy: ['Fire'] }
    }
};

// Aspect-based Western compatibility utilities
export const westernAspects = {
    0: { key: 'conjunction', score: 95, label: 'Conjunction (0°)' },
    2: { key: 'sextile', score: 80, label: 'Sextile (60°)' },
    3: { key: 'square', score: 40, label: 'Square (90°)' },
    4: { key: 'trine', score: 90, label: 'Trine (120°)' },
    5: { key: 'quincunx', score: 30, label: 'Quincunx (150°)' },
    6: { key: 'opposition', score: 75, label: 'Opposition (180°)' },
};

const semiSextileScore = 55; // for d=1 minor aspect (30°)

export function getWesternSignIndexByName(name) {
    return westernZodiac.signs.findIndex(s => s.name === name);
}

export function getAspectBetweenSigns(signA, signB) {
    const iA = typeof signA === 'string' ? getWesternSignIndexByName(signA) : getWesternSignIndexByName(signA?.name);
    const iB = typeof signB === 'string' ? getWesternSignIndexByName(signB) : getWesternSignIndexByName(signB?.name);
    if (iA < 0 || iB < 0) return { aspectKey: 'unknown', d: null };
    const raw = Math.abs(iA - iB);
    const d = Math.min(raw, 12 - raw);
    if (westernAspects[d]) return { aspectKey: westernAspects[d].key, d };
    if (d === 1) return { aspectKey: 'semisextile', d };
    return { aspectKey: 'unknown', d };
}

const aspectDescriptions = {
    conjunction: (a, b) => `Mirroring & amplification. ${a} and ${b} share the same energy, intensifying strengths and shadows.`,
    trine: (a, b) => `Natural harmony & flow. ${a} and ${b} connect easily with shared elemental affinity.`,
    sextile: (a, b) => `Opportunity & easy rapport. ${a} and ${b} stimulate each other with complementary talents.`,
    opposition: (a, b) => `Polarity & attraction. ${a} and ${b} balance opposite qualities; success requires conscious give-and-take.`,
    square: (a, b) => `Dynamic tension & growth. ${a} and ${b} create friction that can drive evolution with effort and respect.`,
    quincunx: (a, b) => `Adjustment & bridging gaps. ${a} and ${b} come from different worlds; patience and flexibility are key.`,
    semisextile: (a, b) => `Subtle adjustment. ${a} and ${b} can collaborate through small compromises and curiosity.`,
    unknown: (a, b) => `Different wavelengths. ${a} and ${b} may need extra context to find rhythm.`,
};

export function calculateWesternAspectCompatibility(signA, signB) {
    const nameA = typeof signA === 'string' ? signA : signA?.name;
    const nameB = typeof signB === 'string' ? signB : signB?.name;
    const { aspectKey, d } = getAspectBetweenSigns(nameA, nameB);
    let score;
    if (westernAspects[d]) {
        score = westernAspects[d].score;
    } else if (aspectKey === 'semisextile') {
        score = semiSextileScore;
    } else if (nameA && nameB && nameA === nameB) {
        score = 95;
    } else {
        score = 50;
    }
    const description = aspectDescriptions[aspectKey]?.(nameA, nameB) || aspectDescriptions.unknown(nameA, nameB);
    return { aspect: aspectKey, score, description };
}

// Numerology data and logic (delegating to shared module)
export const numerology = {
    getLifePathNumber: sharedGetLifePathNumber,

    // Legacy compatibility matrix (updated to remove 28 references)
    compatibility: COMPATIBILITY_MATRIX
};

// User profile creation
export function getUserProfile(name, birthDate) {
    const date = new Date(birthDate);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    
    const lifePathData = numerology.getLifePathNumber(birthDate);
    
    return {
        name: name,
        birthDate: birthDate,
        chineseAnimal: chineseZodiac.getAnimal(year, month, day),
        chineseElement: chineseZodiac.getElement(year),
        westernSign: westernZodiac.getSign(month, day),
        lifePath: lifePathData.value,  // For backward compatibility
        lifePathData: lifePathData     // Full data with isPure and reduction
    };
}

// Generate matches for each category
export function generateCategoryMatches(userProfile) {
    return {
        chinese: generateChineseMatches(userProfile),
        western: generateWesternMatches(userProfile),
        numerology: generateNumerologyMatches(userProfile)
    };
}

function generateChineseMatches(userProfile) {
    const normalizedUserAnimal = ecNormalizeAnimalName(userProfile?.chineseAnimal) || userProfile?.chineseAnimal;
    const base = ecGenerateChineseMatchesBase({ chineseAnimal: normalizedUserAnimal });

    const animalTraits = {
        'Rat': 'Intelligent, adaptable, charming, and quick-witted',
        'Ox': 'Reliable, patient, strong, and determined',
        'Tiger': 'Brave, competitive, unpredictable, and confident',
        'Cat': 'Quiet, elegant, kind, and responsible',
        'Dragon': 'Energetic, intelligent, gifted, and charismatic',
        'Snake': 'Wise, enigmatic, intuitive, and graceful',
        'Horse': 'Animated, active, energetic, and independent',
        'Goat': 'Calm, gentle, sympathetic, and artistic',
        'Monkey': 'Sharp, smart, curious, and inventive',
        'Rooster': 'Observant, hardworking, courageous, and talented',
        'Dog': 'Loyal, responsible, reliable, and honest',
        'Pig': 'Compassionate, generous, diligent, and honest'
    };

    const matches = base.results.map(r => {
        let description = `${r.animal}: ${animalTraits[r.animal]}\n\n`;
        const tierLabel = r.tier === 'same' ? 'SAME ANIMAL'
          : r.tier === 'triad' ? 'TRIAD AFFINITY'
          : r.tier === 'opposite' ? 'OPPOSITES'
          : r.tier === 'challenging' ? 'CHALLENGING'
          : 'NEUTRAL';
        const userAnimal = normalizedUserAnimal || 'Your sign';
        if (r.tier === 'same') {
          description += `🟢 ${tierLabel}: Perfect understanding and natural harmony with another ${r.animal}.`;
        } else if (r.tier === 'triad') {
          description += `🟢 ${tierLabel}: ${userAnimal} and ${r.animal} share a core affinity group.`;
        } else if (r.tier === 'challenging') {
          description += `🟡 ${tierLabel}: ${userAnimal} and ${r.animal} require conscious effort to harmonize.`;
        } else if (r.tier === 'opposite') {
          description += `🔴 ${tierLabel}: ${userAnimal} and ${r.animal} sit opposite on the 12-year wheel.`;
        } else {
          description += `🟠 ${tierLabel}: ${userAnimal} and ${r.animal} have moderate potential with respect.`;
        }
        description += `\n\n(Animal-only base score. Element bonus not applied.)`;
        return { animal: r.animal, score: r.baseScore, description };
    });

    return matches.sort((a, b) => b.score - a.score);
}

function generateWesternMatches(userProfile) {
    const matches = [];
    const signTraits = {
        'Aries': 'Bold, pioneering, energetic, and natural leaders',
        'Taurus': 'Reliable, practical, determined, and sensual',
        'Gemini': 'Curious, adaptable, communicative, and witty',
        'Cancer': 'Nurturing, intuitive, emotional, and protective',
        'Leo': 'Confident, generous, dramatic, and charismatic',
        'Virgo': 'Analytical, helpful, perfectionist, and practical',
        'Libra': 'Diplomatic, balanced, social, and harmony-seeking',
        'Scorpio': 'Intense, passionate, mysterious, and transformative',
        'Sagittarius': 'Adventurous, philosophical, optimistic, and free-spirited',
        'Capricorn': 'Ambitious, disciplined, responsible, and goal-oriented',
        'Aquarius': 'Independent, innovative, humanitarian, and unconventional',
        'Pisces': 'Compassionate, intuitive, artistic, and dreamy'
    };

    for (let sign of westernZodiac.signs) {
        const { aspect, score, description: aspectDesc } = calculateWesternAspectCompatibility(userProfile.westernSign.name, sign.name);

        const description = `${sign.name}: ${signTraits[sign.name]}\n\n${aspectDesc}`;

        matches.push({
            sign: sign.name,
            element: sign.element,
            score: score,
            description: description
        });
    }

    return matches.sort((a, b) => b.score - a.score);
}

function generateNumerologyMatches(userProfile) {
    // Delegate to shared numerology module
    return sharedGenerateNumerologyMatches(userProfile.lifePath);
}